import { formatPhone, titleize } from '@ltvco/refresh-lib/utils';
import PersonIcon from '@mui/icons-material/Person';

import {
  CardContainer,
  CardHeaderRow,
  FieldList,
  ReportFieldRow,
  ReportTitle,
  SectionDivider,
  SeeFullReportCTA,
  ViewReportButton,
} from '../../components';
import { formatAliases, formatDob } from '../utils';

import { PersonLocation } from './components';
import {
  AssetsSection,
  AssociatesSection,
  ContactSection,
  ProfessionalSection,
  RelativesSection,
} from './sections';

import type { PersonReportData } from 'components/DeepSearch/types';

interface PersonReportPreviewCardProps {
  data: PersonReportData;
  onViewReport?: () => void;
  onSendMessage?: (content: string, displayContent?: string) => void;
}

export const PersonReportPreviewCard = ({
  data,
  onViewReport,
  onSendMessage,
}: PersonReportPreviewCardProps) => {
  return (
    <CardContainer>
      <CardHeaderRow sx={{ mb: 1 }}>
        <PersonIcon sx={{ fontSize: 24 }} />
        <ReportTitle m={0}>
          {(data.name && titleize(data.name.toLowerCase())) || 'Person Report'}
        </ReportTitle>
        {onViewReport && <ViewReportButton onClick={onViewReport} />}
      </CardHeaderRow>

      {data.location && (
        <PersonLocation variant="body2">{data.location}</PersonLocation>
      )}

      <SectionDivider />

      <FieldList>
        <ReportFieldRow
          label="Age"
          value={data.age != null ? String(data.age) : 'Not available'}
        />
        <ReportFieldRow
          label="Date of Birth"
          value={data.dob ? formatDob(data.dob) : 'Not available'}
        />
        <ReportFieldRow
          label="Aliases"
          value={
            data.aliases.length
              ? formatAliases(data.aliases)
              : 'No aliases found'
          }
        />
        <ReportFieldRow
          label="Primary Address"
          value={data.primaryAddress || 'No address found'}
        />
        <ReportFieldRow
          label="Primary Phone"
          value={
            data.primaryPhone
              ? formatPhone(data.primaryPhone)
              : 'No phone found'
          }
        />
        <ReportFieldRow
          label="Primary Email"
          value={
            data.primaryEmail
              ? data.primaryEmail.toLowerCase()
              : 'No email address found'
          }
        />
        <ReportFieldRow
          label="Job"
          value={data.job ? titleize(data.job.toLowerCase()) : 'No job found'}
        />
        <ReportFieldRow
          label="Education"
          value={data.education || 'No education found'}
        />
      </FieldList>

      <ContactSection data={data} onSendMessage={onSendMessage} />
      <RelativesSection data={data} onSendMessage={onSendMessage} />
      <AssociatesSection data={data} onSendMessage={onSendMessage} />
      <ProfessionalSection data={data} />
      <AssetsSection data={data} onSendMessage={onSendMessage} />
      {onViewReport && <SeeFullReportCTA onClick={onViewReport} />}
    </CardContainer>
  );
};
